import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  JobsService,
  ModalComponent,
  PersonalizedEmailComponent,
  PopUpService,
  ProfileService,
  StoreService,
  ToastAlertService,
  TrailService
} from 'core/index';
import { Subscription } from 'rxjs';
import { MarsGroupLicenseTableService } from './mars-group-license.service';
import { MarsGroup } from 'app/mars/models/MarsGroup';
import { MarsLicenseService } from 'app/mars/services/mars-licenses.service';
import { BUTTON_ACTIONS_PLAYER_LIST } from 'app/mars/assets/constants';

@Component({
  selector: 'lms-mars-group-license',
  templateUrl: './mars-group-license.component.html',
  styleUrls: ['./mars-group-license.component.scss'],
  providers: [MarsGroupLicenseTableService]
})
export class MarsGroupLicenseComponent implements OnInit, OnChanges, OnDestroy {
  public marsLicenseListId = 'MARS_LICENSE_LIST';
  @Output() uploadPlayers = new EventEmitter<any>();
  @Input() selectable: boolean = true;
  @Input() options: any;
  @Input() showActions?: boolean = true;
  @Input() group: MarsGroup;

  public selectAll: boolean = false;
  public action: string = '';
  public jobs: any;
  public user: any;
  public config: any;
  public toolbarConfig: any;
  public items: any[];
  public players: any[];
  public noPlayers: boolean = false;
  public selectedPlayers: any[] = [];
  public excludedPlayers: any = new Set();

  public listOptions: any = {
    selectable: true,
    hideResendEmail: true,
    active: true
  };

  public didMatchEnd: boolean;
  public dataSource: any;
  public isAnySelected = false;
  public actionItems: any[] = [];
  public hasItems = false;
  private popUpSubscription: Subscription = new Subscription();
  private routeSubscription: Subscription = new Subscription();

  @ViewChild('playersTable', { static: false }) public playersTable: ElementRef;
  @ViewChild('activateModal', { static: false }) public activateModal: ModalComponent;
  @ViewChild('personalizedEmail', { static: false }) public personalizedEmail: PersonalizedEmailComponent;
  @ViewChild('modalCsvUpload', { static: false }) public modalCsvUpload: ModalComponent;

  constructor(
    public marsLicensesService: MarsLicenseService,
    public router: Router,
    public route: ActivatedRoute,
    public translate: TranslateService,
    public marsLicenseTableListService: MarsGroupLicenseTableService,
    public jobsService: JobsService,
    public profileService: ProfileService,
    public trailService: TrailService,
    public popupService: PopUpService,
    public cdr: ChangeDetectorRef,
    public toastService: ToastAlertService
  ) {
    this.jobs = this.jobsService.jobTypes;
  }

  async ngOnInit() {
    this.profileService.getLoggedUser$().subscribe((loggedUser) => {
      this.user = loggedUser;
    });
    this.toolbarConfig = {
      list: { listId: this.marsLicenseListId, download: true, filterRow: true, columnChooser: true, skeletor: true }
    };

    this.actionItems = [...BUTTON_ACTIONS_PLAYER_LIST];
    this.selectable = this.actionItems.some((item) => this.profileService.canExecute(item.profile));
  }

  async ngOnChanges() {
    const trail = this.trailService.getTrailFor(this.marsLicenseListId);
    this.options.filters = { groupId: this.options.filters.groupId };
    this.listOptions = { ...this.options, ...trail };
    await this.initializeDatasource();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.popUpSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();
    StoreService.remove(this.marsLicenseListId);
  }

  onDatasourceUpdate() {
    StoreService.update(this.marsLicenseListId, this.dataSource);
    this.setSelectAll(false);
  }

  async initializeDatasource() {
    if (this.listOptions.selected) {
      this.listOptions.selectionHandler = (data) => this.selectionHandler(data);
    }
    this.dataSource = await this.marsLicenseTableListService.getDatasource(
      this.marsLicenseListId,
      this.listOptions,
      this.group
    );
    if (this.dataSource?.docs?.length) this.hasItems = true;
    if (this.listOptions.selected) {
      this.selectedPlayers = this.dataSource.docs;
      this.isAnySelected = true;
      this.selectAll = true;
    }
  }

  setCollection(collection) {
    this.players = collection;
    this.noPlayers = this.players.length ? false : true;
  }

  selectAction(key) {
    switch (key) {
      case '1':
        this.action = this.jobs.SEND_PERSONALIZED_EMAIL_BULK;
        this.personalizedEmail.openModal();
        break;
      case '2':
        this.action = this.jobs.SEND_M1_EMAIL_BULK;
        this.sendEmail();
        break;
      case '3':
        this.action = this.jobs.ANONYMIZE_PLAYERS_BULK;
        this.confirmAction({
          users: this.setSelectList()
        });
        break;
      case '4':
        this.action = this.jobs.DEACTIVATE_PLAYERS_BULK;
        this.confirmAction({
          users: this.setSelectList()
        });
        break;
    }
  }

  selectionHandler(rowData) {
    if (rowData) {
      if (this.excludedPlayers.has(rowData.id)) return false;
      return (
        this.selectAll || (this.selectedPlayers && this.selectedPlayers.some((student) => student.id === rowData.id))
      );
    }
    return this.selectAll;
  }

  onRowSelectionChanged(eventDetail) {
    const { selected, data } = eventDetail;
    if (selected) {
      this.excludedPlayers.delete(data.id);
    } else {
      this.excludedPlayers.add(data.id);
    }
  }

  onSelectionChanged(selection) {
    const { selected = [] } = selection || {};
    this.selectedPlayers = selected;
    this.isAnySelected = this.selectedPlayers && this.selectedPlayers.length ? true : false;
  }

  onFiltersChanged() {
    this.setSelectAll(false);
  }

  setSelectAll(flag) {
    this.dataSource.selectionHandler = flag ? (data) => this.selectionHandler(data) : null;
    this.excludedPlayers.clear();
    this.selectedPlayers = flag ? this.dataSource.docs : [];
    this.isAnySelected = flag;
    this.selectAll = flag;
    this.playersTable.nativeElement.setAllSelected(flag);
  }

  openModal(action) {
    this.action = action;
    this.activateModal.open();
  }

  confirmModal() {
    this.confirmAction({});
    this.activateModal.close();
  }

  confirmAction(data) {
    data.jobType = this.action;
    this.updatePlayersBulk(data);
  }

  addTags(addedTags: any[]) {
    const data = {
      addedTags,
      removedTags: []
    };
    this.confirmAction(data);
  }

  removeTags(removedTags: any[]) {
    const data = {
      addedTags: [],
      removedTags
    };
    this.confirmAction(data);
  }

  //#region table methods

  private updatePlayersBulk(data: any) {
    const bulkData = {
      query: {
        users: this.setSelectList(),
        search: this.dataSource.search,
        ...this.marsLicenseTableListService.getDatasourceFilters(this.dataSource)
      },
      ...data
    };
    if (this.selectAll) {
      delete bulkData.users;
      if (this.excludedPlayers.size) {
        bulkData.query.excludedPlayers = [...this.excludedPlayers].map((userId) => ({ _id: userId }));
      }
    } else {
      delete bulkData.query;
    }
    this.setSelectAll(false);
    this.marsLicensesService.updateLicensesBulk(bulkData).then(async (res: any) => {
      this.manageBulkJob(res, bulkData.jobType);
    });
  }

  sendEmail(email?: any) {
    const data: any = {
      users: this.setSelectList()
    };
    if (email) {
      data.body = email.body;
      data.title = email.title;
      data.fromName = email.fromName;
      if (email.autosend) {
        // A copy of the personalized email will be sent to the admin
        data.userData = {
          id: this.user.id,
          email: this.user.email
        };
      }
    }
    this.confirmAction(data);
  }

  private setSelectList(): Array<any> {
    return this.selectedPlayers.map((player) => {
      return { _id: player.id };
    });
  }

  //#endregion

  openCsvModal() {
    this.modalCsvUpload.open();
  }

  closeCsvModal() {
    this.modalCsvUpload.close();
  }

  onSaveCsv(csvData: any) {
    this.marsLicensesService.createLicensesBulk(this.group.id, csvData, this.group.clientId).then((res: any) => {
      this.manageBulkJob(res, this.jobsService.jobTypes.CREATE_PLAYERS_BULK);
    });
  }

  manageBulkJob(enrollmentJob, type) {
    const jobSubscription = this.jobsService.manageJob(enrollmentJob.jobId, type).subscribe((job) => {
      if (job.status === 'finished' || job.status === 'fatal-error') {
        this.hasItems = true;
        this.dataSource.refreshData();
        jobSubscription.unsubscribe();
      }
    });
  }

  changeView() {
    this.marsLicenseTableListService.mode = 'view';
    this.dataSource.refreshData();
    this.cdr.detectChanges();
  }

  cancelEditPlayer() {
    this.marsLicenseTableListService.mode = 'view';
  }
}
